// src/pages/Analytics.js
import React, { useEffect, useMemo, useState } from "react";
import EmployeeAnalytics from "../components/Analytics/EmployeeAnalytics";
import TimeAnalytics from "../components/Analytics/TimeAnalytics";
import ScheduleAnalytics from "../components/Analytics/ScheduleAnalytics";
import TrainingAnalytics from "../components/Analytics/TrainingAnalytics";
import LeaveAnalytics from "../components/Analytics/LeaveAnalytics";
import ExpenseAnalytics from "../components/Analytics/ExpenseAnalytics";
import PayrollAnalytics from "../components/Analytics/PayrollAnalytics";
import "../styles/Analytics.css";

import { auth, db } from "../firebase/config";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import Select from "react-select";

// Keep this constant outside the component so it doesn't change every render
const ALL_FEATURE_OPTIONS = [
  { label: "Employee Management" },
  { label: "Time Tracking" },
  { label: "Scheduling" },
  { label: "Training" },
  { label: "Leave" },
  { label: "Expenses" },
  { label: "Payroll" },
];

export default function Analytics({ allowedFeatures = [] }) {
  const [userId, setUserId] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  const [savedFeature, setSavedFeature] = useState(null);
  const [savedFeatureLoaded, setSavedFeatureLoaded] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState("");

  // Employee drill-down filter (used for modules that support it)
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null); // react-select option or null
  const selectedEmployeeId = selectedEmployee?.value || null;

  // ✅ Employee filter should NOT apply to Employee Management analytics
  const showEmployeeFilter =
    selectedFeature && selectedFeature !== "Employee Management";

  // Map labels to plan features, and memoize so the reference is stable
  const filteredOptions = useMemo(
    () =>
      ALL_FEATURE_OPTIONS.filter((opt) =>
        allowedFeatures.includes(
          opt.label === "Leave"
            ? "Leave & Absence Management"
            : opt.label === "Time Tracking"
            ? "Attendance & Time Tracking"
            : opt.label === "Expenses"
            ? "Expense Management"
            : opt.label === "Payroll"
            ? "Reports & Analytics" // gate payroll under your analytics feature
            : opt.label
        )
      ),
    [allowedFeatures]
  );

  // 🔹 Load user + companyId + saved selected feature from users/{uid}
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setUserId(null);
        setCompanyId(null);
        setSavedFeature(null);
        setSavedFeatureLoaded(true);
        return;
      }

      setUserId(user.uid);

      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        const data = snap.data() || {};

        setCompanyId(data.companyId || null);

        if (data.analyticsSelectedFeature) {
          setSavedFeature(data.analyticsSelectedFeature);
        } else {
          setSavedFeature(null);
        }
      } catch (e) {
        console.error("Failed to load analyticsSelectedFeature/companyId", e);
        setCompanyId(null);
        setSavedFeature(null);
      } finally {
        setSavedFeatureLoaded(true);
      }
    });

    return () => unsub();
  }, []);

  // 🔹 Load employees for the drill-down dropdown
  useEffect(() => {
    if (!companyId) {
      setEmployeeOptions([]);
      setSelectedEmployee(null);
      return;
    }

    const loadEmployees = async () => {
      try {
        const snap = await getDocs(
          collection(db, `companies/${companyId}/employees`)
        );

        const opts = snap.docs
          .map((d) => {
            const e = d.data() || {};
            const label =
              `${e.name || ""} ${e.lastName || ""}`.trim() || e.email || d.id;

            return { value: d.id, label };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setEmployeeOptions(opts);
      } catch (err) {
        console.error("Failed to load employees for analytics filter", err);
        setEmployeeOptions([]);
      }
    };

    loadEmployees();
  }, [companyId]);

  // 🔹 Decide which feature should be selected (based on saved + plan)
  useEffect(() => {
    if (!filteredOptions.length) {
      setSelectedFeature("");
      return;
    }

    if (!savedFeatureLoaded) return;

    const isSavedAllowed =
      savedFeature && filteredOptions.some((opt) => opt.label === savedFeature);

    const isCurrentValid =
      selectedFeature &&
      filteredOptions.some((opt) => opt.label === selectedFeature);

    // First-time init: nothing selected yet → use saved or first
    if (!selectedFeature) {
      if (isSavedAllowed) {
        setSelectedFeature(savedFeature);
      } else {
        setSelectedFeature(filteredOptions[0].label);
      }
      return;
    }

    // If plan changed and current selection is no longer valid → fall back
    if (!isCurrentValid) {
      setSelectedFeature(filteredOptions[0].label);
    }
  }, [filteredOptions, savedFeature, savedFeatureLoaded, selectedFeature]);

  // ✅ If user switches to Employee Management, clear employee drill-down
  useEffect(() => {
    if (selectedFeature === "Employee Management" && selectedEmployee) {
      setSelectedEmployee(null);
    }
  }, [selectedFeature, selectedEmployee]);

  // 🔹 Persist selected feature whenever it changes
  useEffect(() => {
    const save = async () => {
      if (!userId || !selectedFeature) return;
      try {
        const userRef = doc(db, "users", userId);
        await setDoc(
          userRef,
          { analyticsSelectedFeature: selectedFeature },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to save analyticsSelectedFeature", e);
      }
    };

    save();
  }, [userId, selectedFeature]);

  const renderSelectedComponent = () => {
    if (!companyId) return <p>Loading company...</p>;

    switch (selectedFeature) {
      case "Employee Management":
        // ✅ company-wide only
        return <EmployeeAnalytics companyId={companyId} />;

      case "Time Tracking":
        return (
          <TimeAnalytics
            companyId={companyId}
            employeeId={selectedEmployeeId}
          />
        );

      case "Scheduling":
        return (
          <ScheduleAnalytics
            companyId={companyId}
            employeeId={selectedEmployeeId}
          />
        );

      case "Training":
        return (
          <TrainingAnalytics
            companyId={companyId}
            employeeId={selectedEmployeeId}
          />
        );

      case "Leave":
        return (
          <LeaveAnalytics
            companyId={companyId}
            employeeId={selectedEmployeeId}
          />
        );

      case "Expenses":
        return (
          <ExpenseAnalytics
            companyId={companyId}
            employeeId={selectedEmployeeId}
          />
        );

      case "Payroll":
        return (
          <PayrollAnalytics
            companyId={companyId}
            employeeId={selectedEmployeeId}
          />
        );

      default:
        return <p>No analytics available.</p>;
    }
  };

  return (
    <div className="analytics-container">
      {filteredOptions.length > 0 ? (
        <>
          <div className="analytics-header"></div>

          <div className="analytics-wrapper">
            {/* Top controls row */}
            {/* Filters */}
            <div className="analytics-filters-card">
              <div className="af-header">
                <div className="af-titleblock">
                  <div className="af-title">Analytics Filters</div>
                  <div className="af-subtitle">
                    {showEmployeeFilter ? (
                      <>
                        Viewing <strong>{selectedFeature}</strong>{" "}
                        {selectedEmployee?.label ? (
                          <>
                            for <strong>{selectedEmployee.label}</strong>
                          </>
                        ) : (
                          <>
                            for <strong>all employees</strong>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        Viewing <strong>{selectedFeature}</strong>{" "}
                        (company-wide)
                      </>
                    )}
                  </div>
                </div>

                {/* Right-side quick actions */}
                <div className="af-actions">
                  {showEmployeeFilter && (
                    <button
                      type="button"
                      className="af-btn af-btn-ghost"
                      onClick={() => setSelectedEmployee(null)}
                      disabled={!selectedEmployee}
                      title="Clear employee filter"
                    >
                      Clear employee
                    </button>
                  )}
                </div>
              </div>

              <div className="af-grid">
                {/* Module: Tabs on desktop */}
                <div className="af-block af-block--moduleTabs">
                  <div className="af-label">Module</div>
                  <div
                    className="af-tabs"
                    role="tablist"
                    aria-label="Analytics module tabs"
                  >
                    {filteredOptions.map((opt) => {
                      const active = opt.label === selectedFeature;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          className={`af-tab ${active ? "is-active" : ""}`}
                          onClick={() => setSelectedFeature(opt.label)}
                          role="tab"
                          aria-selected={active}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Module: Dropdown on mobile */}
                <div className="af-block af-block--moduleSelect">
                  <div className="af-label">Module</div>
                  <select
                    value={selectedFeature}
                    onChange={(e) => setSelectedFeature(e.target.value)}
                    className="af-select"
                  >
                    {filteredOptions.map((opt) => (
                      <option key={opt.label} value={opt.label}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Employee */}
                {showEmployeeFilter && (
                  <div className="af-block">
                    <div className="af-label1">Employee</div>
                    <div className="af-selectWrap">
                      <Select
                        classNamePrefix="oiiko-select"
                        isClearable
                        placeholder="All Employees"
                        options={employeeOptions}
                        value={selectedEmployee}
                        onChange={(opt) => setSelectedEmployee(opt)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="analytics-body">{renderSelectedComponent()}</div>
          </div>
        </>
      ) : (
        <p>No analytics features are available for your current plan.</p>
      )}
    </div>
  );
}
